// Export Control timeline node (app.md §6/§9) — the step-8/9 approval queue
// (File Generation), grouped by company with orange rules between groups.
// The step-11 checkpoint lives in its own Closing Confirmation node
// (ClosingConfirmationScreen).
//
// "Approve & Export" is ONE request. The API builds one file per company, writes
// the copy of record, stamps the batch on each invoice and writes every Export
// Control approval — or it validates the set, refuses, and writes nothing. The
// files come back as signed URLs to save. A held invoice is left out of the
// request and, should one be sent anyway, out of the files by the server; it
// stays in this queue (owner, 2026-09-30, D-247).
//
// PORT NOTE: this used to be a three-phase dance in the browser — build the CSV
// bytes, write an export row and stamp the invoices, write the decisions one at a
// time, then deliver the files and, if no decision had landed, roll the stamps
// back so the retry didn't advance invoices whose file was never delivered.
// Point Click Care parses those bytes, so the format belongs where it can be
// versioned and tested; the whole prepare/rollback apparatus goes with it.
import type { DecisionSource } from '@ap/shared'
import { useCallback } from 'react'
import { Toast, ToastBody, ToastTitle, makeStyles, useToastController } from '@fluentui/react-components'
import { ScreenIntro } from '../components/ScreenIntro'
import { DecisionStep, InvoiceStatus, MAX_IDS_PER_REQUEST, SCREEN_SUBHEADER, TOASTER_ID } from '../lib/constants'
import type { ExportFile, FiscalPick } from '../api/types'
import type { Invoice } from '../lib/types'
import type { Session } from '../lib/identity'
import type { InvoiceFilter } from '../lib/filters'
import type { RefreshSignal } from '../lib/refresh'
import { createExport } from '../lib/data'
import { downloadUrl } from '../lib/download'
import { sentenceList } from '../lib/format'
import { isApiError } from '../api/errors'
import { ApprovalScreen } from './ApprovalScreen'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '20px' },
  // Orange rule under the page's intro (user preference, 2026-06-10) —
  // deliberately distinct from the blue page/timeline rules. The same rule
  // repeats between the company groups (ApprovalScreen groupByCompany).
  rule: { height: '3px', backgroundColor: '#E08A2B', borderRadius: '2px' },
})

interface Props {
  session: Session
  /** The shell's refresh signal (D-160), handed straight to the queue. */
  refresh?: RefreshSignal
  /** Page-wide filter (header Filter button). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ExportControlScreen({ session, refresh, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const { dispatchToast } = useToastController(TOASTER_ID)

  const approveVia = useCallback(
    async (invoices: Invoice[], pick?: FiscalPick, source?: DecisionSource) => {
      if (invoices.length === 0) return { appliedCount: 0 }
      // The controller's pick beside the Export button (owner, 2026-09-23,
      // D-137 and D-138). ApprovalScreen will not send without one; refuse here
      // too rather than let the server answer with a bare 400.
      if (pick === undefined) throw new Error('Pick a fiscal period before exporting.')
      // GUARD (owner item 7 review, F7, 2026-08-19): "Export" now sends the
      // WHOLE company group in one call, not a hand-picked selection — a
      // large company can exceed the server's `MAX_IDS_PER_REQUEST` (200)
      // cap on `POST /exports`. Chunk it: each chunk is its own request and
      // its own transaction, so a later chunk's refusal does not undo an
      // earlier chunk's already-written files and already-stamped invoices.
      const chunks: Invoice[][] = []
      for (let i = 0; i < invoices.length; i += MAX_IDS_PER_REQUEST) {
        chunks.push(invoices.slice(i, i + MAX_IDS_PER_REQUEST))
      }
      const files: ExportFile[] = []
      let appliedCount = 0
      // Held invoices the server left out (D-247). The screen already leaves
      // out every invoice it knows is held; this counts any the server caught.
      let heldCount = 0
      let chunkError: unknown = null
      for (const chunk of chunks) {
        try {
          const result = await createExport(
            chunk.map((i) => i.id),
            pick,
            source,
          )
          files.push(...result.files)
          appliedCount += result.invoices_advanced
          heldCount += result.held_invoice_ids?.length ?? 0
        } catch (e) {
          // Stop at the first refusal — the remaining chunks are not
          // attempted, and everything already committed stays committed.
          chunkError = e
          break
        }
      }
      // Save every file from every chunk that succeeded before any refusal.
      // The stored object is the record; the download is the controller's
      // copy to hand to Point Click Care.
      for (const f of files) {
        if (f.download_url) downloadUrl(f.download_url, f.file_name)
      }
      if (files.length > 0) {
        const missing = files.filter((f) => !f.download_url).map((f) => f.file_name)
        const n = files.length
        dispatchToast(
          <Toast>
            <ToastTitle>
              {n === 1 ? 'One file was built and saved.' : `${n} files were built and saved.`}
            </ToastTitle>
            <ToastBody>
              {missing.length > 0
                ? `These files could not be downloaded: ${sentenceList(missing)}. They were saved, so you can download them from the export batch.`
                : 'Please import the .csv file into Point Click Care, and then confirm at Closing Confirmation.'}
            </ToastBody>
          </Toast>,
          { intent: missing.length > 0 ? 'warning' : 'success', timeout: missing.length > 0 ? 12000 : 8000 },
        )
      }
      if (chunkError) {
        // A refused export carries EVERY problem with the set, not the first —
        // the controller fixes them in one pass rather than one per attempt.
        if (isApiError(chunkError) && chunkError.code === 'validation_failed') {
          const problems = (chunkError.details?.problems as string[] | undefined) ?? []
          const prefix =
            appliedCount > 0
              ? `${appliedCount} invoice${appliedCount === 1 ? '' : 's'} exported before this batch stopped. `
              : 'Nothing was exported. '
          throw new Error(
            problems.length > 0
              ? `${prefix}${problems.length === 1 ? 'One problem' : `${problems.length} problems`} to fix: ${problems.join('; ')}`
              : `${prefix}${chunkError.message}`,
          )
        }
        throw chunkError
      }
      return { appliedCount, heldCount }
    },
    [dispatchToast],
  )

  return (
    <div className={s.root}>
      {/* Intro and rule together, so the rule sits as close under the intro
          as Closing Confirmation's does; the root's gap spaces the rest. */}
      <div>
        <ScreenIntro>{SCREEN_SUBHEADER.ExportControl}</ScreenIntro>
        <div className={s.rule} />
      </div>
      <ApprovalScreen
        session={session}
        refresh={refresh}
        filter={filter}
        onQueuesChanged={onQueuesChanged}
        config={{
          queueStatus: InvoiceStatus.PendingExportControl,
          step: DecisionStep.ExportControl,
          // Just "Export" (owner item 7, 2026-08-19): "Replace the 'Approve &
          // Export Selected' button with just a green 'Export' button." The
          // green fill is `btn.approve` — already applied below via
          // `exportOnly`, unchanged from before this rename.
          approveLabel: 'Export',
          showPdf: true,
          // BOTH HALVES editable here, and NO composed Code column (owner
          // override, 2026-08-18 — `docs/decisions.md`): "I need both 'What Is
          // It?' and 'Who Is It For?' editable, with no 'Account' column at
          // Export Control." ('Account' was the composed column's label
          // then.) A previous pass shipped `editCodes` + `editDepartments`,
          // which left "What is it?" read-only because the server refused a
          // standalone account edit at this step and kept the composed
          // column as the only way to fix one. The owner rejected that.
          // The server's authority matrix (`backend/src/domain/authority.ts`)
          // now grants the standalone account edit here too, so the two
          // half pickers cover everything the composed picker used to, and
          // `InvoiceLineItemsTable`'s column gates drop the Code column the
          // moment `onCodeChange` is no longer passed (no `editCodes`).
          //
          // Changing "What is it?" CLEARS "Who is it for?" (owner ruling,
          // 2026-08-10) — `departmentAfterAccountChange`
          // (`backend/src/domain/codeComposition.ts`) auto-reapplies the
          // sole department for the 76 of 88 active accounts that pin exactly
          // one, so most edits here never show an empty department at all;
          // for the other 16 the "Who is it for?" column is right there,
          // editable, for the controller to re-pick. An uncoded line can never
          // reach Point Click Care either way: the export validates every line
          // item has a code and refuses the whole set, listing each one.
          //
          // BOTH HALVES, ON EITHER ROUTE (owner, 2026-10-07 — D-308: "Export
          // Control should be able to edit Account and Department values;
          // however, at this point in the process, these values should
          // already be filled due to prior steps' requirements."). D-305
          // had locked the Account here; `glCodeEditable` (`lib/required.ts`)
          // reads the one shared rule.
          editAccounts: true,
          editDepartments: true,
          // NO summary box under a company group and NO grand total at the
          // foot of the page (owner, 2026-08-28). What the controller is
          // authorizing is still on the screen: the company header carries a
          // gray "$1,483.37 Total" pill beside its invoice count, the figure
          // the dropped footer summed.
          groupByCompany: true, // one group per company — the file grain (2026-07-12)
          approveVia,
          // Owner item 7, 2026-08-19: no checkboxes, no Select all, no
          // per-row or modal Approve/Deny at this step — Export acts on the
          // whole company group. Hold is back (owner, 2026-09-30, D-247): the
          // row's lock beside Open and Hold in the modal, and a held invoice is
          // left out of the Export and stays here. See
          // `ApprovalConfig.exportOnly`'s own comment.
          exportOnly: true,
          // Fiscal Year (defaulted to the current year) and Fiscal Period
          // (1-12, no default) dropdowns left of each Export button (owner,
          // 2026-09-23, D-137 and D-138): Point Click Care's open period cannot
          // be assumed from the invoice date, so the controller picks it. Export
          // stays disabled until a period is picked, and sends with no
          // confirmation.
          pickFiscalPeriod: true,
        }}
      />
    </div>
  )
}
